import { useMemo } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { DonutChart, Table } from "@similarweb/pro-logic-components";
import type { StrictColor } from "@similarweb/styles";
import { Flex } from "@similarweb/ui-components/foundations";

import { chosenSites } from "app/common/services/chosenSites";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { useWindowSize } from "app/custom-hooks/useWIndowSize";
import { entityTypeMap } from "pro-features/components/AutocompleteAdIntelligence/AutocompleteAdIntelligenceHelper";

import { useDisplayAdvertiserPublisherDataAndParams } from "../../../common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import { usePerformanceQuery } from "../../../common/overview/hooks/usePerformanceQuery";
import { defaultMinHeight, getEmptyState } from "../../../common/overview/PerformanceWidget";
import { normalizePercentages } from "../../../common/overview/utils/graphUtils";
import type { AdIntelligenceController } from "../../../common/shared/types/types";

import { generateDonutTableColumnConfig } from "./columnConfigs";
import { getDonutChartConfig } from "./donutChartConfig";
import { PreviewPerformanceWidgetCompareTable } from "./preview-performance-widget-compare-table";
import { EDataType } from "./types";

const SCREEN_WIDTH_THRESHOLD_SMALL = 1400;
const SCREEN_WIDTH_THRESHOLD_LARGE = 1640;

const PerformanceWidgetsWrapper = styled.div`
    display: grid;
    gap: 24px;
    margin-bottom: 24px;
    grid-template-columns: 1fr 480px;

    @media (max-width: ${SCREEN_WIDTH_THRESHOLD_SMALL}px) {
        grid-template-columns: 1fr 380px;
    }
`;

const DonutChartWrapper = styled(Flex)`
    align-items: center;
    gap: 24px;
    margin: 24px 40px;
`;

const TableContainer = styled.div`
    max-width: 320px;
`;

interface IPreviewPerformanceWidgetCompareProps {
    controller?: AdIntelligenceController;
}

export const PreviewPerformanceWidgetCompare = ({
    controller = entityTypeMap.advertiser,
}: IPreviewPerformanceWidgetCompareProps) => {
    const { translate } = useTranslate();
    const thirdRowEntity = controller === entityTypeMap.advertiser ? EDataType.VISITS : EDataType.PAGE_VIEWS;
    const subtitleFiltersFirst = useSubtitleFilters("date", "country", "webSource");
    const { data: performanceData, isPending, isError, isLoading } = usePerformanceQuery(controller);
    const allParams = useDisplayAdvertiserPublisherDataAndParams();
    const { key } = allParams;
    const windowSize = useWindowSize();

    const comparingDomains = useMemo(() => key.split(","), [key]);
    const chartData = useMemo(() => {
        const domainValues = comparingDomains.map((domain) => {
            const performanceDataItem = performanceData?.records?.[domain];
            return performanceDataItem?.[thirdRowEntity]?.value ?? 0;
        });

        const totalValue = domainValues.reduce((sum, value) => sum + value, 0);
        const rawShares = domainValues.map((value) => (totalValue > 0 ? value / totalValue : 0));
        const normalizedShares = normalizePercentages(rawShares);

        return comparingDomains.map((domain, index) => {
            const color = chosenSites.getSiteColor(domain) as StrictColor;
            const performanceDataItem = performanceData?.records?.[domain];
            const change = performanceDataItem?.[thirdRowEntity]?.change ?? 0;
            const share = normalizedShares[index];

            return {
                domain,
                name: domain,
                id: domain,
                color,
                y: share,
                share: share,
                change: change,
                visible: true,
            };
        });
    }, [comparingDomains, performanceData, thirdRowEntity]);

    const showPerformanceEmptyState =
        !isPending &&
        !isError &&
        !isLoading &&
        !Object.values(performanceData?.records ?? {}).some((record) => record[thirdRowEntity]?.value);

    const chartSize = useMemo(() => {
        const width = windowSize.width;
        if (!width) {
            return { height: 100, width: 100 };
        }

        if (width >= SCREEN_WIDTH_THRESHOLD_LARGE) {
            return { height: 150, width: 150 };
        }
        if (width >= SCREEN_WIDTH_THRESHOLD_SMALL) {
            return { height: 120, width: 120 };
        }
        return { height: 100, width: 100 };
    }, [windowSize.width]);

    const donutTableColumnConfig = generateDonutTableColumnConfig();

    return (
        <PerformanceWidgetsWrapper>
            <WidgetContainer
                title={translate(`ad.intelligence.${controller}.overview.${thirdRowEntity}.widget.title`)}
                tooltip={translate(
                    `ad.intelligence.${controller}.overview.performance.widget.${thirdRowEntity}.tooltip`,
                )}
                subtitleFilters={subtitleFiltersFirst}
                isLoading={isPending}
                loaderWidth="100%"
                loaderHeight={32 + 41 * comparingDomains.length}
            >
                {showPerformanceEmptyState ? (
                    getEmptyState(isError, true, defaultMinHeight)
                ) : (
                    <DonutChartWrapper>
                        <DonutChart
                            innerSize="60%"
                            shouldPresentLegend={false}
                            config={getDonutChartConfig({ chartSize })}
                            data={[
                                {
                                    data: chartData,
                                },
                            ]}
                        />
                        <TableContainer>
                            <Table
                                data={chartData}
                                columnConfig={donutTableColumnConfig}
                                isLoading={false}
                                theme="white"
                                bordersConfig={{ body: false, header: false, rowBorders: false }}
                                rowHoverable={false}
                            />
                        </TableContainer>
                    </DonutChartWrapper>
                )}
            </WidgetContainer>
            <PreviewPerformanceWidgetCompareTable controller={controller} />
        </PerformanceWidgetsWrapper>
    );
};
